/**
 * Detect persisted QMA / comps artifacts on a property record (backend auto-pipeline or user runs).
 *
 * @module utils/propertyReportPresenceUtils
 */

import { hasCompsAnalysisContent } from "@/utils/compsAnalysisUtils";

/**
 * Resolve a value from property → additional_attributes, falling back to defaultValue.
 */
function resolveValue(property, propKey, attrKey, defaultValue) {
  const attrs = property.additional_attributes;
  const attrsObj = attrs && typeof attrs === "object" && !Array.isArray(attrs) ? attrs : null;
  return property[propKey] ?? attrsObj?.[attrKey] ?? defaultValue;
}

/**
 * Check if a value is a non-empty, non-whitespace string.
 */
function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * S3 object key for the backend-generated QMA PDF (integrations bucket).
 * Prefers `qma_pdf_s3_key` from the API, then legacy `pdf_s3_key`.
 *
 * @param {unknown} property
 * @returns {string}
 */
export function resolveQmaReportPdfS3Key(property) {
  if (!property || typeof property !== "object") return "";
  const attrs = property.additional_attributes;
  const attrsObj = attrs && typeof attrs === "object" && !Array.isArray(attrs) ? attrs : null;
  const candidates = [property.qma_pdf_s3_key, attrsObj?.qma_pdf_s3_key, property.pdf_s3_key, attrsObj?.pdf_s3_key];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
}

/**
 * @param {unknown} property
 * @returns {boolean}
 */
export function propertyHasQmaArtifact(property) {
  if (!property || typeof property !== "object") return false;
  const htmlKey = resolveValue(property, "qma_s3_key", "qma_s3_key", "");
  const pdfKey = resolveValue(property, "qma_pdf_s3_key", "qma_pdf_s3_key", "");
  return isNonEmptyString(htmlKey) || isNonEmptyString(pdfKey);
}

/**
 * @param {unknown} property
 * @returns {boolean}
 */
export function propertyHasCompsArtifact(property) {
  if (!property || typeof property !== "object") return false;
  const attrs = property.additional_attributes;
  const attrsObj = attrs && typeof attrs === "object" && !Array.isArray(attrs) ? attrs : null;
  return hasCompsAnalysisContent(property.comps_analysis) || hasCompsAnalysisContent(attrsObj?.comps_analysis);
}

/**
 * True when the property has a persisted SOW artifact (S3 keys or inline report HTML).
 *
 * @param {unknown} property
 * @returns {boolean}
 */
export function propertyHasSowReport(property) {
  if (!property || typeof property !== "object") return false;
  const attrs = property.additional_attributes;
  const attrsObj = attrs && typeof attrs === "object" && !Array.isArray(attrs) ? attrs : null;
  if (isNonEmptyString(property.sow_s3_key) || isNonEmptyString(attrsObj?.sow_s3_key)) return true;
  if (isNonEmptyString(property.sow_pdf_path) || isNonEmptyString(attrsObj?.sow_pdf_path)) return true;
  if (isNonEmptyString(property.sow_estimate_report) || isNonEmptyString(attrsObj?.sow_estimate_report)) {
    return true;
  }
  return false;
}
